import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { BOARD_TABS } from "../data/sampleData";
import { SampleTag } from "./SampleTag";
import { Reveal } from "./Reveal";

const EASE = [0.22, 1, 0.36, 1];

/* Interactive board-room preview: tabs for three exams, four seats,
   and a "Next question" button that cycles five questions and
   highlights the member who is asking. */
export const BoardPreview = () => {
    const [tabId, setTabId] = useState(BOARD_TABS[0].id);
    const [qIndex, setQIndex] = useState(0);

    const tab = BOARD_TABS.find((t) => t.id === tabId);
    const question = tab.questions[qIndex];
    const asker = tab.seats.find((s) => s.id === question.seat);

    return (
        <section className="py-24 md:py-32" data-testid="board-preview">
            <div className="wrap">
                <Reveal className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <p className="eyebrow">Step inside the room</p>
                        <h2 className="mt-4 max-w-xl text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                            Four members. One chair, <em className="italic text-brass">empty.</em>
                        </h2>
                    </div>
                    <SampleTag />
                </Reveal>

                <Reveal className="mt-12">
                    <div className="flex flex-wrap gap-2" role="tablist" aria-label="Choose an exam">
                        {BOARD_TABS.map((t) => (
                            <button
                                key={t.id}
                                role="tab"
                                aria-selected={tabId === t.id}
                                data-testid={`board-tab-${t.id}`}
                                onClick={() => {
                                    setTabId(t.id);
                                    setQIndex(0);
                                }}
                                className={`border px-5 py-2.5 text-[13px] tracking-wide transition-colors duration-300 ${
                                    tabId === t.id
                                        ? "border-pine bg-pine text-paper"
                                        : "border-line bg-transparent text-ink-muted hover:border-brass hover:text-brass"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </Reveal>

                <Reveal delay={0.1} className="mt-8">
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
                        {tab.seats.map((seat) => {
                            const asking = seat.id === asker.id;
                            return (
                                <div
                                    key={seat.id}
                                    data-testid={`seat-${seat.id}`}
                                    className={`border p-5 transition-colors duration-500 ${
                                        asking
                                            ? "border-brass bg-brass-soft"
                                            : "border-line bg-surface"
                                    }`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <p className="font-display text-base leading-snug sm:text-lg">
                                            {seat.name}
                                        </p>
                                        <span
                                            className={`inline-block h-1.5 w-1.5 shrink-0 rotate-45 transition-colors duration-500 ${
                                                asking ? "bg-brass" : "bg-line"
                                            }`}
                                        />
                                    </div>
                                    <p className="mt-2 text-xs leading-relaxed text-ink-muted">
                                        {seat.note}
                                    </p>
                                    <p
                                        className={`mt-4 text-[10px] font-medium uppercase tracking-[0.2em] transition-opacity duration-500 ${
                                            asking ? "text-brass" : "opacity-0"
                                        }`}
                                    >
                                        Asking
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-3 h-px w-full bg-line md:mt-4" aria-hidden="true" />
                </Reveal>

                <Reveal delay={0.15} className="mt-10">
                    <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
                        <div className="lg:col-span-8">
                            <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-brass">
                                Question {qIndex + 1} of {tab.questions.length} · {asker.name} asking
                            </p>
                            <AnimatePresence mode="wait">
                                <motion.blockquote
                                    key={`${tab.id}-${qIndex}`}
                                    initial={{ opacity: 0, y: 14 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -14 }}
                                    transition={{ duration: 0.45, ease: EASE }}
                                    data-testid="board-question"
                                    className="mt-4 font-display text-2xl leading-snug tracking-tight sm:text-3xl lg:text-[2.1rem]"
                                >
                                    “{question.text}”
                                </motion.blockquote>
                            </AnimatePresence>
                        </div>
                        <div className="lg:col-span-4 lg:text-right">
                            <button
                                type="button"
                                data-testid="next-question-btn"
                                onClick={() => setQIndex((i) => (i + 1) % tab.questions.length)}
                                className="btn-primary"
                            >
                                Next question
                                <ArrowRight size={15} strokeWidth={1.5} />
                            </button>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
